import { toSignal } from '@angular/core/rxjs-interop';
import { inject, Injectable } from '@angular/core';
import { BaseHttpService } from './base-http.service';
import { finalize, map, Observable, switchMap } from 'rxjs';
import { ActionDto, ActionsCountsDto, ActionStatusDto, ActionKindDto } from '@shared/interfaces/dtos/actions';
import {
  Action,
  ActionProcessQuery,
  ActionsCounts,
  ActionsQuery,
  ActionStatus,
  CreateAction,
} from '@shared/interfaces/actions';
import { ActionScope, ActionType } from '@shared/types/actions';
import { ActionsMapper, ObjectUtils } from '@shared/utils';
import { PaginatedItems } from '@shared/types/dev';
import { UserInfo } from '@shared/models/user-info';
import { iAdminService } from 'src/app/core/services/iAdmin.service';

@Injectable({
  providedIn: 'root',
})
export class ActionsService extends BaseHttpService {
  private readonly userInfos = toSignal<UserInfo>(inject(iAdminService).getUserInfo());

  protected endPoint: string = 'Points/Actions';
  private pointsEndpoint = this.endPoint;
  private meetingsEndpoint = 'Seances/Actions';

  private readonly tags = ['All', 'Todo', 'Traite', 'Transmission'];

  public insertOneAction(target: { id: number; scope: 'point' | 'meeting' }, dto: CreateAction): Observable<unknown> {
    if (target.scope === 'point') {
      return this.http.post(this.buildURL({ overrideEndPoint: 'Points', idOrSubEndpoint: `${target.id}/Users` }), dto);
    }

    return this.http.post(this.buildURL({ overrideEndPoint: 'Seances', idOrSubEndpoint: `${target.id}/Users` }), {
      ...dto,
    });
  }

  private fetchPointsAllCategoriesCounts(): Observable<ActionsCounts> {
    this.resetEndpoint();

    return this.http
      .get<ActionsCountsDto>(this.buildURL({ idOrSubEndpoint: 'Count' }), { params: { tags: this.tags.join(',') } })
      .pipe(
        map((counts) => ({
          toDo: counts.actionToDo,
          done: counts.actionTraite,
          all: counts.actionAll,
          askedByMe: counts.actionTransmission,
        }))
      );
  }

  private fetchMeetingsAllCategoriesCounts(): Observable<ActionsCounts> {
    this.resetEndpoint();

    this.endPoint = this.meetingsEndpoint;
    return this.http
      .get<ActionsCountsDto>(this.buildURL({ idOrSubEndpoint: 'Count' }), { params: { tags: this.tags.join(',') } })
      .pipe(
        map((counts) => ({
          toDo: counts.actionToDo,
          done: counts.actionTraite,
          all: counts.actionAll,
          askedByMe: counts.actionTransmission,
        })),
        finalize(() => (this.endPoint = this.pointsEndpoint))
      );
  }

  public fetchActionKinds(): Observable<ActionKindDto[]> {
    return this.http.get<ActionKindDto[]>(this.buildURL({ overrideEndPoint: 'Global/Actions/Types' }));
  }

  public fetchAllCategoriesCounts(scope: ActionScope = 'points'): Observable<ActionsCounts> {
    return scope === 'points' ? this.fetchPointsAllCategoriesCounts() : this.fetchMeetingsAllCategoriesCounts();
  }

  public fetchActions(
    init: {
      scope: ActionScope;
      type: ActionType;
      query?: Omit<ActionsQuery, 'CalcTotal' | 'Traite' | 'ContactId'>;
    },
    options?: { proccessableAsFirst: boolean }
  ): Observable<PaginatedItems<Action>> {
    const { proccessableAsFirst = true } = options ?? {};

    return this.iAdminService.getUserInfo().pipe(
      switchMap((userInfo) => {
        let { scope, type, query = init.query ?? {} } = init;

        const queryToUse: ActionsQuery = {
          ...query,
          CalcTotal: true,
          Traite: type == 'done' ? true : type === 'to-do' ? false : undefined,
          ContactId: userInfo.contactId,
        };

        if (type === 'from-me') {
          delete queryToUse.ContactId;
          queryToUse.TransmissionUserId = userInfo.id;
        }

        ObjectUtils.removeFalsyEntries(queryToUse);

        if (scope === 'meetings') this.endPoint = this.meetingsEndpoint;

        return this.http
          .get<PaginatedItems<ActionDto>>(this.buildURL({ idOrSubEndpoint: 'Search' }), { params: { ...queryToUse } })
          .pipe(
            map((res) => {
              return {
                totalCount: res.totalCount,
                totalPages: res.totalPages,
                pageNumber: res.pageNumber,
                pageSize: res.pageSize,
                items: res.items.map((item) => ActionsMapper.toAction(item, scope, type)),
              };
            }),
            map((res) => {
              if (proccessableAsFirst) {
                const processable = (a: Action) => {
                  return (
                    a.demander.id === this.userInfos().id && a.recipient.id === this.userInfos().contactId && !a.done
                  );
                };

                return {
                  totalCount: res.totalCount,
                  totalPages: res.totalPages,
                  pageNumber: res.pageNumber,
                  pageSize: res.pageSize,
                  items: res.items.sort((_, b) => (processable(b) ? 1 : -1)),
                };
              }

              return res;
            })
          );
      })
    );
  }

  public fetchActionStatuses(): Observable<ActionStatus[]> {
    this.endPoint = 'Global/Actions';
    return this.http.get<ActionStatusDto[]>(this.buildURL({ idOrSubEndpoint: 'Statutes' })).pipe(
      map((res) => res.map((status) => ({ label: status.description, value: status.description }))),
      finalize(() => (this.endPoint = this.pointsEndpoint))
    );
  }

  public processAction(config: {
    actionId: number;
    scope: ActionScope;
    pointOrMeetingId: number;
    query: ActionProcessQuery;
  }): Observable<unknown> {
    const { actionId, scope, pointOrMeetingId, query } = config;

    return this.http.post(
      this.buildURL({
        overrideEndPoint: `${scope === 'meetings' ? 'Seances' : 'Points'}/${pointOrMeetingId}/Actions/${actionId}/Reply`,
      }),
      { ...query }
    );
  }

  private resetEndpoint(): void {
    this.endPoint = 'Points/Actions';
  }
}
